"use client";

import { useRouter, usePathname } from "next/navigation";
import type { Lang } from "@/proxy";
import { apiMethods } from "@/lib/services/api";

const TRANSLATABLE_ROUTES = ["blog", "services", "projects"];

export function useLocalizedSlug() {
  const router = useRouter();
  const pathname = usePathname();

  const getTranslatedPath = async (targetLang: Lang): Promise<string> => {
    const segments = pathname.split("/").filter(Boolean);
    
    if (segments.length < 2) {
      return `/${targetLang}`;
    }

    const currentLang = segments[0] as Lang;
    if (currentLang !== "en" && currentLang !== "ar") {
      return `/${targetLang}/${segments.slice(1).join("/")}`;
    }

    const routeType = segments[1];
    if (!TRANSLATABLE_ROUTES.includes(routeType)) {
      const newSegments = [targetLang, ...segments.slice(1)];
      return `/${newSegments.join("/")}`;
    }

    if (segments.length < 3) {
      return `/${targetLang}/${routeType}`;
    }

    const currentSlug = segments[2];
    const endpoint = routeType === "blog"
      ? `/blogs/slug/${currentSlug}?lang=${currentLang}`
      : routeType === "services"
        ? `/services/slug/${currentSlug}?lang=${currentLang}`
        : `/projects/slug/${currentSlug}?lang=${currentLang}`;

    try {
      const res = await apiMethods.get<{ data: { slug: { [key: string]: string } } }>(endpoint);
      const translatedSlug = res?.data?.slug?.[targetLang];
      
      if (translatedSlug) {
        return `/${targetLang}/${routeType}/${translatedSlug}`;
      }
    } catch {
      // fallback to current slug
    }

    return `/${targetLang}/${routeType}/${currentSlug}`;
  };

  const switchLang = async (targetLang: Lang) => {
    const newPath = await getTranslatedPath(targetLang);
    router.push(newPath);
  };

  return { switchLang, getTranslatedPath };
}